import { useState } from "react";
import api from "../../lib/api";
import CollaboratorPicker from "../CollaboratorPicker";
import { StatePill } from "../CollaboratorBits";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import DeleteWithReasonDialog from "../DeleteWithReasonDialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { useOrgMutation } from "./useOrgMutation";
import { memberCountLabel, serviceDeactivationWarning } from "../../lib/collaborators";

export default function ServicesPanel({ refs, canManage }) {
  const { busy, run } = useOrgMutation(refs);
  const { user } = useAuth();
  const canDelete = canManage && hasPermission(user, "services:delete");
  const [deleting, setDeleting] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | service
  const [toggling, setToggling] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false); // separate from `toggling`: the dialog keeps its text while it fades out
  const [name, setName] = useState("");
  const [head, setHead] = useState({ id: "", label: "" });
  const [deputy, setDeputy] = useState({ id: "", label: "" });

  const open = (service) => {
    setEditing(service);
    setName(service === "new" ? "" : service.name);
    setHead(service === "new" || !service.head?.id ? { id: "", label: "" } : { id: service.head.id, label: service.head.display_name });
    setDeputy(service === "new" || !service.deputy?.id ? { id: "", label: "" } : { id: service.deputy.id, label: service.deputy.display_name });
  };

  const save = async () => {
    const body = { name: name.trim(), head_id: head.id || null, deputy_id: deputy.id || null };
    const ok = editing === "new"
      ? await run(() => api.post("/services", body), "Service créé")
      : await run(() => api.patch(`/services/${editing.id}`, { expected_version: editing.version, ...body }), "Service mis à jour");
    if (ok) setEditing(null);
  };

  const toggle = async () => {
    const service = toggling;
    const ok = await run(() => api.patch(`/services/${service.id}`, { expected_version: service.version, active: !service.active }), service.active ? "Service désactivé" : "Service réactivé");
    if (ok) setConfirmOpen(false);
  };

  const lockedHint = (service, which) => (service[which]?.hidden ? "Hors de votre périmètre" : service[which]?.display_name);

  return (
    <div className="space-y-3" data-testid="services-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Chaque service peut avoir un responsable et un remplaçant.</p>
        {canManage && (
          <Button onClick={() => open("new")} className="shrink-0" data-testid="btn-service-new">
            <Plus size={16} className="mr-1.5" /> Nouveau service
          </Button>
        )}
      </div>
      {refs.services.length === 0 ? (
        <Card className="p-8 text-center space-y-2" data-testid="services-empty">
          <p className="font-medium text-slate-900">Aucun service pour le moment</p>
          <p className="text-sm text-slate-500">Créez vos services pour rattacher les personnes à leur équipe.</p>
        </Card>
      ) : (
        <Card className="p-0 divide-y divide-slate-100">
          {refs.services.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-3 p-4" data-testid={`service-row-${s.id}`}>
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-slate-900 break-words">{s.name}</span><StatePill active={s.active} />
                  {memberCountLabel(s.member_count) && <span className="text-xs text-slate-500" data-testid={`service-count-${s.id}`}>· {memberCountLabel(s.member_count)}</span>}</div>
                <div className="text-xs text-slate-500">Responsable : {lockedHint(s, "head") || "aucun"} · Remplaçant : {lockedHint(s, "deputy") || "aucun"}</div>
              </div>
              {canManage && (
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" aria-label={`Modifier ${s.name}`} onClick={() => open(s)} data-testid={`btn-service-edit-${s.id}`}><Pencil size={15} /></Button>
                  <Button variant="ghost" size="icon" aria-label={`${s.active ? "Désactiver" : "Réactiver"} ${s.name}`} onClick={() => { setToggling(s); setConfirmOpen(true); }} data-testid={`btn-service-toggle-${s.id}`}><Power size={15} /></Button>
                  {canDelete && <Button variant="ghost" size="icon" className="text-red-700" aria-label={`Supprimer ${s.name}`} onClick={() => setDeleting(s)} data-testid={`btn-service-delete-${s.id}`}><Trash2 size={15} /></Button>}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="service-dialog">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nouveau service" : "Modifier le service"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label htmlFor="svc-name">Nom *</Label>
              <Input id="svc-name" className="text-base md:text-sm" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="input-service-name" /></div>
            <div className="space-y-2"><Label>Responsable du service</Label>
              <CollaboratorPicker testid="service-head" value={head.id} label={head.label} onChange={(id, label) => setHead({ id: id || "", label: label || "" })} /></div>
            <div className="space-y-2"><Label>Remplaçant</Label>
              <CollaboratorPicker testid="service-deputy" value={deputy.id} label={deputy.label} onChange={(id, label) => setDeputy({ id: id || "", label: label || "" })} /></div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Annuler</Button>
            <Button onClick={save} disabled={!name.trim() || busy} data-testid="btn-service-save">
              {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteWithReasonDialog
        target={deleting} testId="delete-service" onClose={() => setDeleting(null)}
        title={`Supprimer le service « ${deleting?.name || ""} » ?`}
        description="Il va dans la corbeille et se restaure pendant 30 jours. Un service auquel des personnes sont rattachées (fiches archivées comprises) ne peut pas être supprimé : désactivez-le à la place."
        onConfirm={async (reason) => {
          const target = deleting;
          setDeleting(null);
          await run(() => api.delete(`/services/${target.id}`, { data: { reason } }), "Service mis à la corbeille");
        }}
      />
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="service-toggle-dialog">
          <DialogHeader>
            <DialogTitle>{toggling?.active ? `Désactiver « ${toggling?.name} » ?` : `Réactiver « ${toggling?.name} » ?`}</DialogTitle>
            <DialogDescription>
              {toggling?.active
                ? "Les personnes déjà rattachées à ce service le restent, mais il ne pourra plus recevoir de nouvelles personnes. Vous pourrez le réactiver."
                : "Le service pourra de nouveau recevoir des personnes."}
            </DialogDescription>
            {toggling && serviceDeactivationWarning(toggling) && (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="service-toggle-warning">{serviceDeactivationWarning(toggling)}</p>
            )}
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={toggle} disabled={busy} data-testid="btn-service-toggle-confirm">
              {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} {toggling?.active ? "Désactiver" : "Réactiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
